import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import GoogleAuthButton from "../../components/GoogleAuthButton";

const FIELDS = [
  { id: "salon_name", label: "Salon name", type: "text", required: true },
  { id: "contact_name", label: "Professional contact name", type: "text", required: true },
  { id: "email", label: "Business email", type: "email", required: true },
  { id: "country", label: "Country", type: "text", required: true },
  { id: "vat_number", label: "VAT number (when applicable)", type: "text", required: false },
  { id: "billing_address", label: "Billing address", type: "text", required: false },
];

export default function SalonRegister() {
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (id, v) => setForm((f) => ({ ...f, [id]: v }));

  const submit = async () => {
    for (const f of FIELDS) {
      if (f.required && !(form[f.id] || "").trim()) return toast.error(`${f.label} is required`);
    }
    if (password !== confirm) return toast.error("Passwords do not match");
    if (!/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{10,}$/.test(password)) {
      return toast.error("Password must be at least 10 characters with upper, lower and a number.");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/salons/register`, { ...form, password });
      await axios.post(`${API}/salons/login`, { email: form.email, password });
      toast.success("Salon account created");
      nav("/salon/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register salon");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="salon-register">
      <div className="max-w-xl mx-auto px-6 py-8">
        <Link to="/salon" className="text-[11px] kt-tracking-wider uppercase text-[#6F6B66]">
          ← Back to Hairtu Professional
        </Link>
        <div className="mt-10">
          <div className="text-[10px] kt-tracking-wider uppercase text-[#6F6B66] mb-3">
            — Salon subscription · €30 / month
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight text-[#383633] tracking-tight mb-3">
            Register your salon.
          </h1>
          <p className="text-[#6F6B66] mb-8">
            Only business information is needed. You'll add your payment method on the next step.
          </p>
        </div>

        {/* Google prefill — populates Salon name, email, and contact name so
            the professional doesn't have to retype them. Password is still
            required. */}
        <div className="mb-6" data-testid="salon-register-google">
          <GoogleAuthButton
            variant="light"
            testid="salon-register-google-btn"
            onSuccess={({ email, name }) => {
              setForm((f) => ({
                ...f,
                email: email || f.email || "",
                contact_name: name || f.contact_name || "",
                salon_name: f.salon_name || name || "",
              }));
              toast.success(`Signed in as ${email} — fill in the remaining details`);
            }}
          />
          <div className="mt-4 flex items-center gap-3 text-[10px] kt-tracking-wider uppercase text-[#8A857E]">
            <div className="flex-1 h-px bg-[#ECE7DF]" />
            <span>or use email</span>
            <div className="flex-1 h-px bg-[#ECE7DF]" />
          </div>
        </div>

        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.id}>
              <Label htmlFor={f.id} className="text-xs kt-tracking uppercase text-[#6F6B66]">
                {f.label}{f.required ? " *" : ""}
              </Label>
              <Input
                id={f.id}
                data-testid={`input-${f.id}`}
                type={f.type}
                value={form[f.id] || ""}
                onChange={(e) => set(f.id, e.target.value)}
                className="mt-2 border-[#ECE7DF] bg-white rounded-xl h-12 focus-visible:ring-[#383633]"
              />
            </div>
          ))}
          <div>
            <Label htmlFor="password" className="text-xs kt-tracking uppercase text-[#6F6B66]">
              Password *
            </Label>
            <Input id="password" data-testid="input-password" type="password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-2 border-[#ECE7DF] bg-white rounded-xl h-12 focus-visible:ring-[#383633]" />
            <p className="mt-2 text-[11px] text-[#6F6B66]">
              At least 10 characters, one uppercase, one lowercase, one number.
            </p>
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs kt-tracking uppercase text-[#6F6B66]">
              Confirm password *
            </Label>
            <Input id="confirm" data-testid="input-password-confirm" type="password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 border-[#ECE7DF] bg-white rounded-xl h-12 focus-visible:ring-[#383633]" />
          </div>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="salon-register-submit"
            className="w-full h-14 rounded-full bg-[#383633] hover:bg-[#4A4238] text-white text-xs kt-tracking uppercase"
          >
            {busy ? "Creating account…" : "Create salon account"}
          </Button>
        </div>
      </div>
    </div>
  );
}
